import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from 'moment';
import swal from "sweetalert";
import { Dropdown, Tab, Nav } from "react-bootstrap";
import SelectableContext from "@restart/ui/SelectableContext";
import { getUpcomingBookings, getBookingDetails, getStatusDropdown, getSingleVehicleDetail } from "../../../services/TransferService";
import { canEditBooking, EDIT_LOCK_HOURS } from "../../../utils/booking";

// This theme's .btn-warning/.btn-primary/.btn-danger classes don't map to their semantic
// names (.btn-warning is orange, and .btn-primary + .btn-danger are the exact same red),
// so status colors are set with explicit inline colors instead of trusting those classes.
// Matched by label text rather than status id since the id-to-label mapping isn't documented.
const STATUS_STYLES = {
	schedule: { backgroundColor: '#FFC107', color: '#000' },  // yellow
	completed: { backgroundColor: '#28a745', color: '#fff' }, // green
	cancelled: { backgroundColor: '#dc3545', color: '#fff' }, // red
	noShow: { backgroundColor: '#fd7e14', color: '#fff' },    // orange
	confirmed: { backgroundColor: '#496ecc', color: '#fff' }, // blue
	pending: { backgroundColor: '#6c757d', color: '#fff' },   // grey
};

function getStatusStyle(label) {
	const normalized = (label || '').toLowerCase();
	if (normalized.includes('schedul')) return STATUS_STYLES.schedule;
	if (normalized.includes('complet')) return STATUS_STYLES.completed;
	if (normalized.includes('cancel')) return STATUS_STYLES.cancelled;
	if (normalized.includes('no-show') || normalized.includes('no show')) return STATUS_STYLES.noShow;
	if (normalized.includes('confirm')) return STATUS_STYLES.confirmed;
	return STATUS_STYLES.pending;
}

const TABS = [
	{ key: 'Upcoming', title: 'Upcoming' },
	{ key: 'Completed', title: 'Completed', type: 'Completed,No-Show' },
	{ key: 'Cancelled', title: 'Cancelled', type: 'Cancelled' },
];

const DEFAULT_DAYS = 90;
const PAGE_SIZE = 100;

function PaxTags({ booking }) {
	return (
		<div className="d-flex align-items-center flex-wrap" style={{ gap: '0.4rem', fontSize: '0.75rem' }}>
			<span className="badge light bg-light text-dark"><i className="fa fa-user me-1" />{booking.no_of_pax}</span>
			{Number(booking.extra_luggage) > 0 && (
				<span className="badge light bg-light text-dark"><i className="fa fa-suitcase me-1" />{booking.extra_luggage}</span>
			)}
			{Number(booking.baby_seats) > 0 && <span className="badge light bg-light text-dark">{booking.baby_seats}C</span>}
			{Number(booking.baby_booster) > 0 && <span className="badge light bg-light text-dark">{booking.baby_booster}B</span>}
		</div>
	);
}

function RowActions({ booking, onEdit }) {
	const editable = canEditBooking(booking);

	function handleClick() {
		if (editable) {
			onEdit(booking);
			return;
		}
		swal(
			"Can't edit this booking",
			`Please note that this booking is currently within the ${EDIT_LOCK_HOURS}-hour timeframe. For any amendments, please email the request to sales@flymiles.com.au`,
			"warning"
		);
	}

	// Corrections doc: editable = green (#16591d), non-editable = red.
	const editColor = editable ? "#16591d" : "#dc3545";

	return (
		<button
			type="button"
			className="btn btn-sm rounded-circle bg-white"
			style={{
				width: "32px",
				height: "32px",
				padding: 0,
				lineHeight: 1,
				borderColor: editColor,
				color: editColor,
			}}
			onClick={handleClick}
			title="Edit booking"
		>
			<i className="fa fa-pen" />
		</button>
	);
}

function getVehicleName(vehicle) {
	return vehicle
		? vehicle.vehicle_type_des || vehicle.vehicle_type_name || `${vehicle.make || ''} ${vehicle.model || ''}`.trim()
		: '';
}

// Everything the row displays, lower-cased, for the table search box.
function getSearchText(b, statusLabels, vehicleMap) {
	const pickup = moment(b.booking_date, 'YYYY-MM-DD HH:mm:ss');
	return [
		b.bookingid,
		b.user_name,
		b.user_phone,
		b.from_address,
		b.to_address,
		b.flight_number,
		b.agency_ref_no,
		b.agency_staff,
		b.currency,
		b.price_total,
		Number(b.price_total || 0).toFixed(2),
		statusLabels[b.status] || 'Pending',
		getVehicleName(vehicleMap[b.vehicle_id]),
		pickup.isValid() ? pickup.format('DD/MM/YYYY hh:mm A') : '',
		b.booking_date,
	]
		.map((v) => (v == null ? '' : String(v)))
		.join(' ')
		.toLowerCase();
}

function TransfersTable({ rows, loading, tabTitle, searchText, sortOrder, statusLabels, vehicleMap, onEdit }) {
	const [page, setPage] = useState(0);

	const filtered = React.useMemo(() => {
		let result = rows;
		// Match every word of the query against all the text shown in the row, so e.g.
		// "john sydney", "12/10/2026" or "completed" all work.
		const terms = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
		if (terms.length) {
			result = result.filter((b) => {
				const haystack = getSearchText(b, statusLabels, vehicleMap);
				return terms.every((t) => haystack.includes(t));
			});
		}
		result = [...result].sort((a, b) => {
			const aMoment = moment(a.booking_date, 'YYYY-MM-DD HH:mm:ss');
			const bMoment = moment(b.booking_date, 'YYYY-MM-DD HH:mm:ss');
			// Bookings with an unparseable date sort last instead of producing a NaN diff.
			const aTime = aMoment.isValid() ? aMoment.valueOf() : -Infinity;
			const bTime = bMoment.isValid() ? bMoment.valueOf() : -Infinity;
			const diff = aTime - bTime;
			return sortOrder === 'Newest' ? -diff : diff;
		});
		return result;
	}, [rows, searchText, sortOrder, statusLabels, vehicleMap]);

	useEffect(() => setPage(0), [rows, searchText]);

	const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const currentPage = Math.min(page, pageCount - 1);
	const pageRows = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

	if (loading) {
		return <div className="text-center text-muted py-5">Loading bookings...</div>;
	}

	if (filtered.length === 0) {
		return (
			<div className="table-responsive">
				<div className="text-center text-muted py-5">No {tabTitle.toLowerCase()} bookings found.</div>
			</div>
		);
	}

	return (
		<div className="table-responsive">
			<div className="dataTables_wrapper no-footer">
				<table className="table card-table display mb-4 shadow-hover default-table dataTablesCard dataTable no-footer">
					<thead>
						<tr role="row">
							<th className="sorting_asc">Conf#</th>
							<th className="sorting_asc">Pickup</th>
							<th className="sorting">Name</th>
							<th className="sorting">Pickup - Dropoff</th>
							<th className="sorting">Status</th>
							<th className="sorting">Price</th>
							<th className="sorting bg-none"></th>
						</tr>
					</thead>
					<tbody>
						{pageRows.map((b) => {
							const vehicleName = getVehicleName(vehicleMap[b.vehicle_id]);
							const statusLabel = statusLabels[b.status] || 'Pending';
							const statusStyle = getStatusStyle(statusLabel);
							const pickup = moment(b.booking_date, 'YYYY-MM-DD HH:mm:ss');
							return (
								<tr role="row" key={b.id}>
									<td>
										<Link to={"#"} className="text-primary fw-500">{b.bookingid}</Link>
									</td>
									<td>
										<div>
											<span className="font-w500 d-block">Pickup:</span>
											<span className="text-nowrap d-block">{pickup.isValid() ? pickup.format('DD/MM/YYYY') : '—'}</span>
											<span className="d-block">{pickup.isValid() ? pickup.format('hh:mm A') : ''}</span>
										</div>
									</td>
									<td>
										<div>
											<h5 className="fs-16 mb-0 text-nowrap">
												<Link to={"#"} className="text-orange">{b.user_name}</Link>
											</h5>
											<span className="fs-14 d-block">{b.user_phone}</span>
											{vehicleName && <span className="font-w500 fs-14 d-block">{vehicleName}</span>}
											<PaxTags booking={b} />
										</div>
									</td>
									<td>
										<div style={{ minWidth: '220px' }}>
											<span className="font-w500 d-block">Pickup</span>
											<span className="fs-14 d-block mb-2">{b.from_address}</span>
											<span className="font-w500 d-block">Dropoff</span>
											<span className="fs-14 d-block">{b.to_address}</span>
											{(() => {
												const flightNo = String(b.flight_number ?? '').trim();
												const hasFlight = flightNo !== '' && flightNo !== '-' && flightNo.toLowerCase() !== 'null';
												const agencyRef = String(b.agency_staff ?? '').trim();
												const hasAgencyRef = agencyRef !== '' && agencyRef !== '-' && agencyRef.toLowerCase() !== 'null';
												if (!hasFlight && !hasAgencyRef) return null;
												return (
													<div className="text-white rounded px-2 py-1 mt-2 fs-13" style={{ backgroundColor: '#16591d' }}>
														{hasFlight && <i className="fa fa-plane me-1" />}
														{hasFlight && flightNo}
														{hasFlight && hasAgencyRef && ' | '}
														{hasAgencyRef && `Ref: ${agencyRef}`}
													</div>
												);
											})()}
										</div>
									</td>
									<td>
										<Link to={"#"} className="btn btn-md" style={statusStyle}>{statusLabel}</Link>
									</td>
									<td>
										<span className="font-w500">{b.currency} {Number(b.price_total || 0).toFixed(2)}</span>
									</td>
									<td><RowActions booking={b} onEdit={onEdit} /></td>
								</tr>
							);
						})}
					</tbody>
				</table>
				<div className="d-sm-flex text-center justify-content-between align-items-center mt-3 mb-3">
					<div className="dataTables_info">
						Showing {currentPage * PAGE_SIZE + 1} to{" "}
						{Math.min((currentPage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} entries
					</div>
					<div className="dataTables_paginate paging_simple_numbers mb-0">
						<Link
							className={`paginate_button previous ${currentPage === 0 ? 'disabled' : ''}`}
							to="/transfers"
							onClick={(e) => { e.preventDefault(); currentPage > 0 && setPage(currentPage - 1); }}
						>
							<i className="fa fa-angle-double-left" aria-hidden="true"></i>
						</Link>
						<span>
							{Array.from({ length: pageCount }, (_, i) => (
								<Link
									key={i}
									className={`paginate_button ${currentPage === i ? "current" : ""}`}
									to="/transfers"
									onClick={(e) => { e.preventDefault(); setPage(i); }}
								>
									{i + 1}
								</Link>
							))}
						</span>
						<Link
							className={`paginate_button next ${currentPage >= pageCount - 1 ? 'disabled' : ''}`}
							to="/transfers"
							onClick={(e) => { e.preventDefault(); currentPage + 1 < pageCount && setPage(currentPage + 1); }}
						>
							<i className="fa fa-angle-double-right" aria-hidden="true"></i>
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}

const Transfers = () => {
	const navigate = useNavigate();
	const agent = useSelector((state) => state.auth.auth) || {};

	const [activeTab, setActiveTab] = useState('Upcoming');
	const [searchText, setSearchText] = useState('');
	const [sortOrder, setSortOrder] = useState('Oldest');
	const [customRange, setCustomRange] = useState(null); // null = use default 90-day window (send `days`, not from/to)
	const [fromDateInput, setFromDateInput] = useState('');
	const [toDateInput, setToDateInput] = useState('');
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [statusLabels, setStatusLabels] = useState({});
	const [vehicleMap, setVehicleMap] = useState({});
	const vehicleCacheRef = useRef({});

	const currentTab = TABS.find((t) => t.key === activeTab);
	const showDateFilter = !!currentTab.type;

	useEffect(() => {
		getStatusDropdown().then(setStatusLabels).catch(() => {});
	}, []);

	useEffect(() => {
		if (!agent.id) return;
		let cancelled = false;
		setLoading(true);

		const request = currentTab.type
			? getBookingDetails({
					agentId: agent.id,
					type: currentTab.type,
					days: DEFAULT_DAYS,
					fromDate: customRange ? customRange.start.format('YYYY-MM-DD') : undefined,
					toDate: customRange ? customRange.end.format('YYYY-MM-DD') : undefined,
			  })
			: getUpcomingBookings(agent.id);

		request
			.then(async (data) => {
				if (cancelled) return;
				const missingIds = [...new Set(data.map((b) => b.vehicle_id))].filter(
					(id) => id && !vehicleCacheRef.current[id]
				);
				if (missingIds.length > 0) {
					const details = await Promise.all(missingIds.map((id) => getSingleVehicleDetail(id).catch(() => null)));
					const updates = {};
					missingIds.forEach((id, i) => {
						vehicleCacheRef.current[id] = details[i] || {};
						if (details[i]) updates[id] = details[i];
					});
					if (!cancelled && Object.keys(updates).length) {
						setVehicleMap((prev) => ({ ...prev, ...updates }));
					}
				}
				if (!cancelled) setRows(data);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, customRange, agent.id]);

	function handleTabSelect(key) {
		setActiveTab(key);
		setCustomRange(null);
		setSearchText('');
		setFromDateInput('');
		setToDateInput('');
	}

	function handleDateSearch() {
		if (!fromDateInput || !toDateInput) {
			swal('Select both dates', 'Please choose both a From and To date to search.', 'warning');
			return;
		}
		const start = moment(fromDateInput, 'YYYY-MM-DD');
		const end = moment(toDateInput, 'YYYY-MM-DD');
		if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
			swal('Invalid date range', 'The "To" date must be the same as or after the "From" date.', 'warning');
			return;
		}
		setCustomRange({ start, end });
	}

	function handleEdit(booking) {
		navigate('/transfer-edit-booking', { state: { bookingId: booking.id } });
	}

	return (
		<>
			<Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
				<div className="d-flex justify-content-between align-items-center flex-wrap">
					<div className="card-action coin-tabs mb-2">
						<Nav as="ul" className="nav nav-tabs">
							{TABS.map((tab) => (
								<Nav.Item as="li" className="nav-item" key={tab.key}>
									<Nav.Link className="nav-link" eventKey={tab.key}>{tab.title}</Nav.Link>
								</Nav.Item>
							))}
						</Nav>
					</div>
					{/* Reset SelectableContext here: react-bootstrap's Dropdown shares the same
						SelectableContext as Tab.Container/Nav, so without this, clicking a
						Dropdown.Item bubbles an onSelect(undefined) up to the Tab.Container,
						which crashes the page (activeTab becomes undefined). */}
					<SelectableContext.Provider value={null}>
					<div className="d-flex align-items-center mb-2 flex-wrap" style={{ gap: '0.75rem' }}>
						<input
							type="text"
							className="form-control"
							placeholder="Search within table..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							style={{ width: '12rem' }}
						/>
						{showDateFilter && (
							<div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
								<div className="input-group" style={{ width: 'auto', flexShrink: 0 }}>
									<span className="input-group-text">From</span>
									<input
										type="date"
										className="form-control"
										value={fromDateInput}
										onChange={(e) => setFromDateInput(e.target.value)}
										style={{ minWidth: '9rem' }}
									/>
								</div>
								<div className="input-group" style={{ width: 'auto', flexShrink: 0 }}>
									<span className="input-group-text">To</span>
									<input
										type="date"
										className="form-control"
										value={toDateInput}
										onChange={(e) => setToDateInput(e.target.value)}
										style={{ minWidth: '9rem' }}
									/>
								</div>
								<button type="button" className="btn btn-primary" onClick={handleDateSearch}>
									<i className="fa fa-search me-1" />Search
								</button>
							</div>
						)}
						<div className="newest">
							<Dropdown>
								<Dropdown.Toggle as="div" className="btn-select-drop default-select btn i-false">
									{sortOrder} <i className="fas fa-angle-down ms-2"></i>
								</Dropdown.Toggle>
								<Dropdown.Menu>
									<Dropdown.Item onClick={() => setSortOrder("Oldest")}>Oldest</Dropdown.Item>
									<Dropdown.Item onClick={() => setSortOrder("Newest")}>Newest</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</div>
					</SelectableContext.Provider>
				</div>
				<div className="row mt-4">
					<div className="col-xl-12">
						<div className="card">
							<div className="card-body p-0">
								<Tab.Content>
									{TABS.map((tab) => (
										<Tab.Pane eventKey={tab.key} key={tab.key}>
											{activeTab === tab.key && (
												<TransfersTable
													rows={rows}
													loading={loading}
													tabTitle={tab.title}
													searchText={searchText}
													sortOrder={sortOrder}
													statusLabels={statusLabels}
													vehicleMap={vehicleMap}
													onEdit={handleEdit}
												/>
											)}
										</Tab.Pane>
									))}
								</Tab.Content>
							</div>
						</div>
					</div>
				</div>
			</Tab.Container>
		</>
	);
};

export default Transfers;
